import type { ReactNode } from "react"

import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline"
import { clsx } from "clsx"

import type {
  BatteryProposalFinancialSummaryDto,
  BatteryFinancingSummaryDto,
  BatteryFinancingTermsDto,
  BatteryVppBenefitProgramDto,
} from "~/api/dto/battery-proposal.dto"

import {
  financialSummaryLedgerLines,
  FinancialSummaryIncentives,
  hasFinancialSummaryIncentives,
} from "../../ProposalPresentation/pricing/financial-summary-ledger"
import {
  CardEyebrow,
  FooterLabel,
  LedgerRows,
  NetSystemCostFooter,
  reduction,
  SummaryCardLayout,
  SummaryHeading,
} from "../../ProposalPresentation/pricing/summary-card"
import {
  STALE_FIGURE_CLASS,
  STALE_FIGURES_NOTE,
  STALE_FIGURES_WITHIN_CLASS,
} from "../../ProposalPresentation/updates/stale-figure"
import { OutOfDateNotice } from "../pricing-out-of-date"
import {
  formatEscalator,
  TPO_PRODUCT_TYPE_LABEL,
  tpoTermsText,
} from "../tpo-terms-copy"
import { formatUsdExact } from "../view-helpers"

export {
  FooterLabel,
  SummarySlot,
} from "../../ProposalPresentation/pricing/summary-card"

export {
  APPLIED_TO_CONTRACT_HEADING,
  MAY_ALSO_QUALIFY_HEADING,
} from "../../ProposalPresentation/pricing/financial-summary-ledger"

export const ENERGY_REBATES_DISCLOSURE = `Upon Your request, Powur will provide You with reasonable support and documentation that may be necessary in connection with Your application for any federal, state, or local energy rebate or tax credit available in connection with installation of the Battery System; provided however that POWUR MAKES NO REPRESENTATION, WARRANTY, OR GUARANTEE THAT YOU WILL QUALIFY FOR OR WILL OBTAIN ANY FEDERAL, STATE, OR LOCAL ENERGY REBATE OR TAX CREDIT IN CONNECTION WITH THE BATTERY SYSTEM. You are advised to consult legal and/or accounting professionals regarding Your eligibility for and assistance with any such energy rebate or tax credit. Powur is not responsible for changes in availability or incentives of any federal, state, or local energy rebate program or tax credit.`

export const VPP_BENEFIT_EYEBROW = `Estimated VPP benefits`
export const VPP_MONTHLY_BENEFIT_LABEL = `Est. VPP monthly benefit`
export const VPP_NET_EFFECTIVE_PAYMENT_LABEL = `Net effective payment`
export const VPP_TOTAL_BENEFIT_LABEL = `Total estimated VPP benefit`
export const VPP_LEARN_MORE_LABEL = `Learn more`
export const ESTIMATED_MONTHLY_PAYMENT_LABEL = `Estimated monthly payment`
export const SUMMARY_PAYMENT_OUT_OF_DATE_LABEL = `Out of date`
export const SUMMARY_PAYMENT_UNAVAILABLE_LABEL = `Payment unavailable`
export const SUMMARY_PAYMENT_UNAVAILABLE_PROMPT = `A monthly payment is unavailable for this Loan Product. Refresh pricing to calculate it.`
export const SUMMARY_TPO_PAYMENT_UNAVAILABLE_PROMPT = `A monthly payment is unavailable for this offer. Save the offer again to quote it.`
/** Ties an asterisked payment figure to the note beneath it. */
export const PAYMENT_FOOTNOTE_MARKER = `*`

/**
 * The net effective payment, when it is a figure of its own: the server has to
 * have priced both payments, and a benefit that rounds away leaves the two
 * identical, which reads as a contradiction rather than a saving.
 */
export const vppNetEffectivePayment = (
  vpp: BatteryProposalFinancialSummaryDto[`vpp`],
): { netEffective: number; owedToLender: number } | null => {
  if (
    vpp?.netEffectiveMonthlyPayment == null ||
    vpp.actualMonthlyPayment == null ||
    formatUsdExact(vpp.monthlyBenefit) === formatUsdExact(0)
  ) {
    return null
  }
  return {
    netEffective: vpp.netEffectiveMonthlyPayment,
    owedToLender: vpp.actualMonthlyPayment,
  }
}

/**
 * The gradient investment card: the stored ledger beside its incentives. It
 * renders figures exactly as the server returned them and owns no requests, so
 * the public presentation and the seller brochure share one rendering. Seller
 * extras arrive through the `header` and `actions` slots. Estimated monthly
 * payment comes from one saved financing summary, so every Finance Partner
 * uses the same footer.
 */
export function FinancialSummaryLedgerCard({
  summary,
  financingSummary = null,
  header = null,
  actions = null,
  className,
  testId = `battery-financial-summary`,
  staleMarks,
}: {
  summary: BatteryProposalFinancialSummaryDto
  financingSummary?: BatteryFinancingSummaryDto | null
  header?: ReactNode
  actions?: ReactNode
  className?: string
  testId?: string
  /**
   * The seller page marks out-of-date figures with a dotted gold underline,
   * as Solar does, and its Updates panel carries the fix: every ledger figure
   * while pricing is out of date, otherwise a stale payment alone. Omitted,
   * a stale payment keeps its pill.
   */
  staleMarks?: { readonly pricing: boolean }
}): JSX.Element {
  // VPP estimates live in this column too, so a proposal carrying only VPP
  // still gets the column — even though VPP is not an incentive deduction.
  const hasIncentives =
    hasFinancialSummaryIncentives(summary) || summary.vpp != null

  return (
    <SummaryCardLayout
      testId={testId}
      className={className}
      header={header}
      incentivesTestId="battery-financial-summary-incentives"
      ledger={
        <>
          {staleMarks?.pricing === true ? (
            <div
              data-testid="battery-financial-summary-stale"
              className={STALE_FIGURES_WITHIN_CLASS}
            >
              <p className="sr-only">{STALE_FIGURES_NOTE}</p>
              <Ledger
                summary={summary}
                financingSummary={financingSummary}
                underlineStale
              />
            </div>
          ) : (
            <Ledger
              summary={summary}
              financingSummary={financingSummary}
              underlineStale={staleMarks != null}
            />
          )}
          {actions}
        </>
      }
      incentives={hasIncentives ? <IncentivesDetail summary={summary} /> : null}
    />
  )
}

/**
 * A TPO Finance Selection reads as its offer type, term and escalator. It has
 * no APR, so none is ever printed for it.
 */
export const financingTermsLabel = (terms: BatteryFinancingTermsDto): string =>
  terms.tpo != null
    ? `${TPO_PRODUCT_TYPE_LABEL[terms.tpo.productType]} with ${terms.productLabel} · ${tpoTermsText({ termYears: terms.termYears, escalationRate: terms.tpo.escalationRate })}`
    : terms.apr == null
      ? `Financed with ${terms.productLabel} · ${terms.termYears}-year term`
      : `Financed with ${terms.productLabel} · ${terms.termYears}-year term · ${terms.apr.toFixed(2)}% APR`

/**
 * A Sungage BrightStart payment assumes the Deferred Payment Portion is repaid.
 * Same disclosure as the solar Sungage loan summary.
 */
export const sungageDeferredPaymentFootnote = (
  terms: BatteryFinancingTermsDto,
  maxMonthlyPayment: number,
): string =>
  `The above assumes a ${terms.termYears}-year term${terms.apr == null ? `` : ` and ${terms.apr.toFixed(2)}% APR`}. If you do not repay the Deferred Payment Portion by the reamortization date, the balance will be added to the Monthly Payment Portion of your loan and your monthly payment will increase to an estimated amount of ${formatUsdExact(maxMonthlyPayment)}. Always consult with a tax professional regarding your individual tax situation and expectations for receiving the tax credit.`

/** The qualification under a payment figure, when its terms carry one. */
const paymentTermsFootnote = (
  terms: BatteryFinancingTermsDto | null | undefined,
): { text: string; testId: string } | null =>
  terms?.tpo != null
    ? {
        text: tpoFirstYearPaymentFootnote(terms.tpo.escalationRate),
        testId: `battery-tpo-payment-footnote`,
      }
    : terms?.sungage != null
      ? {
          text: sungageDeferredPaymentFootnote(
            terms,
            terms.sungage.maxMonthlyPayment,
          ),
          testId: `battery-sungage-payment-footnote`,
        }
      : null

/** A TPO payment escalates yearly, so the figure shown is only year one's. */
export const tpoFirstYearPaymentFootnote = (escalationRate: number): string =>
  `First-year payment. It rises ${formatEscalator(escalationRate)} each year.`

/**
 * Ledger lines closing on Net system cost. The Loan Product behind the payment
 * reads as a subtitle so the whole ledger is framed by its financing. Like the
 * payment, the terms of a non-current Quote stay on screen but never print.
 *
 * The payment column shares the footer row with Net system cost — except when a
 * VPP benefit buys the payment down, which moves all three payment figures
 * into the VPP block so the subtraction reads in one place.
 */
function Ledger({
  summary,
  financingSummary,
  underlineStale,
}: {
  summary: BatteryProposalFinancialSummaryDto
  financingSummary: BatteryFinancingSummaryDto | null
  underlineStale: boolean
}): JSX.Element {
  const lines = financialSummaryLedgerLines(summary)
  const terms = financingSummary?.terms
    ? financingTermsLabel(financingSummary.terms)
    : null
  const termsPrintable = financingSummary?.status === `current`
  const netEffective =
    financingSummary?.status === `current`
      ? vppNetEffectivePayment(summary.vpp)
      : null
  const showPayment = netEffective == null
  return (
    <div>
      <SummaryHeading>
        {terms ? (
          <FinancingTermsLine
            label={terms}
            printable={termsPrintable}
            testId="battery-financing-terms"
          />
        ) : null}
      </SummaryHeading>

      {summary.vpp ? (
        <VppMonthlyBenefit
          vpp={summary.vpp}
          netEffective={netEffective}
          paymentFootnote={paymentTermsFootnote(financingSummary?.terms)}
        />
      ) : null}

      <LedgerRows lines={lines} />

      <NetSystemCostFooter netSystemCost={summary.netSystemCost}>
        {showPayment ? (
          <LedgerPayment
            financingSummary={financingSummary}
            underlineStale={underlineStale}
          />
        ) : null}
      </NetSystemCostFooter>
    </div>
  )
}

/**
 * The financing the ledger is framed by, as a subtitle under its heading. Like
 * the payment, the terms of a non-current Quote stay on screen but never print.
 */
export function FinancingTermsLine({
  label,
  printable,
  testId,
}: {
  label: string
  printable: boolean
  testId: string
}): JSX.Element {
  return (
    <p
      data-testid={testId}
      className={clsx(
        `mt-2 text-[length:var(--step--1)] text-white/85`,
        !printable && `print:hidden`,
      )}
    >
      {label}
    </p>
  )
}

/**
 * The payment column beside Net system cost. A footnote marks the figure with
 * an asterisk and carries the qualification beneath it — the payment still
 * owed to the lender, or the reason no payment was priced.
 *
 * Seller Quotes and the homeowner saved projection both use this figure so
 * switching Finance Partners does not change the footer layout. A caller that
 * has the server's reason for an unavailable payment passes it, and the figure
 * reads "Payment unavailable" with that reason instead of $0.
 */
export function LedgerPayment({
  financingSummary,
  unavailableReason = null,
  termsFootnote,
  underlineStale = false,
}: {
  financingSummary: BatteryFinancingSummaryDto | null
  unavailableReason?: string | null
  /** Replaces the qualification the terms carry; omitted, the terms decide it. */
  termsFootnote?: { text: string; testId: string } | null
  /** A stale payment is underlined rather than badged with a pill. */
  underlineStale?: boolean
}): JSX.Element | null {
  if (financingSummary == null) return null
  const stale = financingSummary.status === `stale`
  const unpriced = financingSummary.status === `unavailable`
  const explained = unpriced && unavailableReason != null
  const tpo = financingSummary.terms?.tpo ?? null
  return (
    <PaymentFigure
      label={ESTIMATED_MONTHLY_PAYMENT_LABEL}
      amount={explained ? null : (financingSummary.estimatedMonthlyPayment ?? 0)}
      testId="battery-estimated-monthly-payment"
      footnote={
        unpriced
          ? {
              // Refresh Pricing never re-quotes a TPO offer; a new save does.
              text:
                unavailableReason ??
                (tpo != null
                  ? SUMMARY_TPO_PAYMENT_UNAVAILABLE_PROMPT
                  : SUMMARY_PAYMENT_UNAVAILABLE_PROMPT),
              testId: `battery-payment-unavailable-footnote`,
              alert: true,
            }
          : termsFootnote !== undefined
            ? termsFootnote
            : paymentTermsFootnote(financingSummary.terms)
      }
      figureClassName={stale && underlineStale ? STALE_FIGURE_CLASS : undefined}
      badge={
        !stale ? null : underlineStale ? (
          // What a screen reader hears in place of the underline.
          <span className="sr-only" data-testid="battery-payment-out-of-date">
            {SUMMARY_PAYMENT_OUT_OF_DATE_LABEL}
          </span>
        ) : (
          <OutOfDateNotice
            label={SUMMARY_PAYMENT_OUT_OF_DATE_LABEL}
            testId="battery-payment-out-of-date"
          />
        )
      }
      hideFromPrint={financingSummary.status !== `current`}
    />
  )
}

/**
 * The payment column beside Net system cost. A footnote marks the figure with
 * an asterisk and carries the qualification beneath it — the payment still
 * owed to the lender, or the reason no payment was priced.
 */
export function PaymentFigure({
  label,
  amount,
  testId,
  footnote = null,
  badge = null,
  hideFromPrint = false,
  figureClassName,
}: {
  label: string
  /** Null reads "Payment unavailable" in place of a figure. */
  amount: number | null
  testId: string
  footnote?: { text: string; testId: string; alert?: boolean } | null
  badge?: ReactNode
  hideFromPrint?: boolean
  figureClassName?: string
}): JSX.Element {
  // The footnote is a footer row of its own rather than part of the payment
  // column: a whole sentence in that column widens it past the ledger and
  // drops the figure off the Net system cost line.
  return (
    <>
      <div
        data-testid="battery-financial-summary-payment"
        className={clsx(
          `flex max-w-sm flex-col items-start sm:items-end sm:text-right`,
          hideFromPrint && `print:hidden`,
        )}
      >
        <FooterLabel>{label}</FooterLabel>
        <div className="mt-1.5 flex flex-wrap items-end gap-3 sm:justify-end">
          <div
            data-testid={testId}
            className={clsx(
              `font-display text-[length:var(--step-2)] leading-none font-bold tracking-[-0.02em] tabular-nums`,
              figureClassName,
            )}
          >
            {amount == null ? (
              <span className="text-[length:var(--step-0)] font-semibold tracking-normal">
                {SUMMARY_PAYMENT_UNAVAILABLE_LABEL}
              </span>
            ) : (
              <>
                {formatUsdExact(amount)}
                <span className="text-[length:var(--step--1)] font-medium text-white/85">
                  /mo
                </span>
              </>
            )}
            {footnote ? (
              <span
                aria-hidden="true"
                className="align-super text-[length:var(--step--1)] font-medium text-white/85"
              >
                {PAYMENT_FOOTNOTE_MARKER}
              </span>
            ) : null}
          </div>
          {badge}
        </div>
      </div>
      {footnote ? (
        <p
          role={footnote.alert === true ? `alert` : undefined}
          data-testid={footnote.testId}
          className={clsx(
            `-mt-2 w-full text-[length:var(--step--2)] leading-relaxed text-white/75 sm:text-right`,
            hideFromPrint && `print:hidden`,
          )}
        >
          {PAYMENT_FOOTNOTE_MARKER}
          {footnote.text}
        </p>
      ) : null}
    </>
  )
}

/**
 * The payment arithmetic the benefit performs: what the lender is owed, the
 * benefit that comes off it, and the effective payment that leaves. Cash and
 * an unusable Quote both arrive with no priced payment, so those get the
 * benefit line alone rather than a manufactured subtraction.
 *
 * Sits above the ledger rows on purpose: Net system cost closes the column,
 * and a block underneath it took that closing position away.
 *
 * Every figure here is rendered exactly as the server calculated it.
 */
function VppMonthlyBenefit({
  vpp,
  netEffective,
  paymentFootnote,
}: {
  vpp: NonNullable<BatteryProposalFinancialSummaryDto["vpp"]>
  netEffective: ReturnType<typeof vppNetEffectivePayment>
  /** Qualifies the lender payment: a TPO's year one, or a Sungage deferral. */
  paymentFootnote: { text: string; testId: string } | null
}): JSX.Element {
  return (
    <div data-testid="battery-vpp-monthly-benefit" className="mb-7">
      {/* The lender payment leads: the benefit beneath it reads as what comes
          off that payment, and the total line resolves the two. */}
      {netEffective ? (
        <div className="mb-3 flex items-baseline justify-between gap-6">
          <FooterLabel>{ESTIMATED_MONTHLY_PAYMENT_LABEL}</FooterLabel>
          <span
            data-testid="battery-vpp-actual-monthly-payment"
            className="font-display text-[length:var(--step-1)] font-semibold tracking-[-0.015em] whitespace-nowrap tabular-nums"
          >
            {formatUsdExact(netEffective.owedToLender)}
            <span className="text-[length:var(--step--2)] font-normal">/mo</span>
          </span>
        </div>
      ) : null}
      <div className="flex items-baseline justify-between gap-6">
        <FooterLabel>{VPP_MONTHLY_BENEFIT_LABEL}</FooterLabel>
        <span
          data-testid="battery-vpp-monthly-benefit-value"
          className="font-display text-[length:var(--step-1)] font-semibold tracking-[-0.015em] whitespace-nowrap tabular-nums"
        >
          {/* Signed like a ledger deduction: the benefit comes off the
              payment above it. */}
          {reduction(vpp.monthlyBenefit)}
          <span className="text-[length:var(--step--2)] font-normal">/mo</span>
        </span>
      </div>
      {netEffective ? (
        <div className="mt-3 flex items-baseline justify-between gap-6 border-t border-white/20 pt-3">
          <FooterLabel>{VPP_NET_EFFECTIVE_PAYMENT_LABEL}</FooterLabel>
          <span
            data-testid="battery-vpp-net-effective-payment"
            className="font-display text-[length:var(--step-2)] font-bold tracking-[-0.02em] whitespace-nowrap tabular-nums"
          >
            {formatUsdExact(netEffective.netEffective)}
            <span className="text-[length:var(--step--2)] font-normal">/mo</span>
          </span>
        </div>
      ) : null}
      {netEffective && paymentFootnote != null ? (
        <p
          data-testid={paymentFootnote.testId}
          className="mt-2 text-[length:var(--step--2)] leading-relaxed text-white/75"
        >
          {paymentFootnote.text}
        </p>
      ) : null}
    </div>
  )
}

function IncentivesDetail({
  summary,
}: {
  summary: BatteryProposalFinancialSummaryDto
}): JSX.Element {
  return (
    <div>
      <FinancialSummaryIncentives
        summary={summary}
        totalTestId="battery-total-incentives"
      />
      {summary.vpp ? <VppProjectedBenefit vpp={summary.vpp} /> : null}
      {/* Last in the column, whatever else the card carries: this is the
          standing legal disclosure for the section, not a note about the block
          above it. */}
      <p
        data-testid="battery-energy-rebates-disclosure"
        className="mt-5 text-[length:var(--step--2)] leading-relaxed text-white/75"
      >
        <span className="font-bold text-white">
          Energy Rebates and Tax Credits.
        </span>
        {` `}
        {ENERGY_REBATES_DISCLOSURE}
      </p>
    </div>
  )
}

/**
 * Each selected VPP program's projected ten-year benefit, with its own total.
 *
 * Kept visually and arithmetically separate from Total Incentives: these are
 * projected payments to the homeowner, not reductions in the price, and they
 * are deliberately absent from Net system cost.
 *
 * The server still sends `vpp.disclosures`; presentation deliberately does not
 * render it (product decision, 2026-09-09). The period stays visible on each
 * program row instead. Re-rendering it is a one-line change here.
 */
function VppProjectedBenefit({
  vpp,
}: {
  vpp: NonNullable<BatteryProposalFinancialSummaryDto["vpp"]>
}): JSX.Element | null {
  // The public share and the print route render outside every error boundary,
  // so these guards are total rather than merely finite-checking: a shape
  // surprise here is a blank page for the homeowner, not a caught fallback.
  const programs = Array.isArray(vpp.programs) ? vpp.programs : []
  const years =
    Number.isFinite(vpp.estimationMonths) && vpp.estimationMonths > 0
      ? vpp.estimationMonths / 12
      : null

  if (programs.length === 0) return null

  return (
    <div
      data-testid="battery-financial-summary-vpp"
      className="mt-6 border-t border-white/25 pt-5"
    >
      <CardEyebrow className="mb-3">{VPP_BENEFIT_EYEBROW}</CardEyebrow>
      <ul className="m-0 list-none p-0">
        {programs.map((program) => (
          <li
            key={program.vppProgramId}
            className="flex items-baseline justify-between gap-6 border-b border-white/20 py-3 first:pt-0"
          >
            <VppProgramDetail program={program} years={years} />
            <span className="font-display text-[length:var(--step-0)] font-semibold tracking-[-0.01em] whitespace-nowrap tabular-nums">
              {formatUsdExact(program.totalBenefit)}
            </span>
          </li>
        ))}
      </ul>
      <div className="flex items-baseline justify-between gap-6 pt-3.5">
        <span className="font-display text-[length:var(--step-0)] font-semibold tracking-[-0.015em]">
          {VPP_TOTAL_BENEFIT_LABEL}
        </span>
        <span
          data-testid="battery-vpp-total-benefit"
          className="font-display text-[length:var(--step-1)] font-bold tracking-[-0.02em] whitespace-nowrap tabular-nums"
        >
          {formatUsdExact(vpp.totalBenefit)}
        </span>
      </div>
    </div>
  )
}

/**
 * A program's name with what the program is and where to read more, both from
 * the program's own configuration. The server sends an empty description and a
 * null link when the program has none, so either simply drops out.
 */
function VppProgramDetail({
  program,
  years,
}: {
  program: BatteryVppBenefitProgramDto
  years: number | null
}): JSX.Element {
  const description =
    typeof program.description === `string` ? program.description.trim() : ``
  // The server already admits only http(s) links; this keeps an unexpected
  // shape out of an href on routes that render outside every error boundary.
  const learnMoreUrl =
    typeof program.learnMoreUrl === `string` &&
    /^https?:\/\//i.test(program.learnMoreUrl)
      ? program.learnMoreUrl
      : null
  return (
    <div className="min-w-0 break-words">
      <span className="text-[length:var(--step--1)] text-white/95">
        {program.label}
      </span>
      {years == null ? null : (
        <span className="block text-[length:var(--step--2)] text-white/70">
          {`Estimated over ${years} years`}
        </span>
      )}
      {description ? (
        <p
          data-testid="battery-vpp-program-description"
          className="mt-1.5 text-[length:var(--step--2)] leading-relaxed whitespace-pre-line text-white/85"
        >
          {description}
        </p>
      ) : null}
      {learnMoreUrl ? (
        <a
          data-testid="battery-vpp-program-learn-more"
          href={learnMoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1.5 inline-flex items-center gap-1 rounded-sm text-[length:var(--step--2)] font-semibold text-white underline decoration-white/50 underline-offset-2 hover:decoration-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          {VPP_LEARN_MORE_LABEL}
          <span className="sr-only">{` about ${program.label} (opens in a new tab)`}</span>
          <ArrowTopRightOnSquareIcon aria-hidden="true" className="size-3.5" />
        </a>
      ) : null}
    </div>
  )
}
